import { Link } from "react-router-dom";
import { FileText, ShieldCheck, Mail } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { legal } from "@/legal";

function Section({ index, s, contactLabel }) {
  return (
    <Reveal delay={0.04 * Math.min(index, 5)}>
      <article className="grid grid-cols-[auto,1fr] gap-4 sm:gap-6" data-testid={`legal-section-${index}`}>
        <span className="font-display font-extrabold text-2xl sm:text-3xl text-[#ABCF39] leading-none pt-1 w-10 sm:w-12">{String(index + 1).padStart(2, "0")}</span>
        <div>
          <h3 className="font-display font-bold text-lg sm:text-xl text-[#383838]">{s.h}</h3>
          {s.p?.map((t) => (
            <p key={t} className="mt-3 text-sm sm:text-base leading-relaxed text-slate-600">{t}</p>
          ))}
          {s.list && (
            <ul className="mt-3 space-y-1.5">
              {s.list.map((li) => (
                <li key={li} className="flex items-start gap-2.5 text-sm sm:text-base text-slate-600">
                  <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-[#ABCF39] shrink-0" />
                  {li}
                </li>
              ))}
            </ul>
          )}
          {s.after?.map((t) => (
            <p key={t} className="mt-3 text-sm sm:text-base leading-relaxed text-slate-600">{t}</p>
          ))}
          {s.facts && (
            <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {s.facts.map(([k, v]) => (
                <div key={k} className="rounded-2xl bg-[#FAFAFA] border border-slate-200/80 px-4 py-3">
                  <dt className="text-xs font-semibold text-slate-400">{k}</dt>
                  <dd className="mt-1 text-sm font-bold text-[#383838]">{v}</dd>
                </div>
              ))}
            </dl>
          )}
          {s.email && (
            <a href={`mailto:${s.email}`} data-testid="legal-contact-email" className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#383838] text-white px-4 py-2 text-sm font-semibold hover:bg-[#ABCF39] hover:text-[#383838] transition-colors">
              <Mail size={14} />
              <span className="sr-only">{contactLabel}: </span>
              <span dir="ltr">{s.email}</span>
            </a>
          )}
        </div>
      </article>
    </Reveal>
  );
}

export default function LegalPage({ kind }) {
  const { lang } = useLanguage();
  const L = legal[lang];
  const doc = L[kind];
  const other = kind === "terms" ? "privacy" : "terms";
  const Icon = kind === "terms" ? FileText : ShieldCheck;

  return (
    <main data-testid={`legal-page-${kind}`} className="relative pt-32 sm:pt-40 pb-20 sm:pb-28 bg-white grain">
      <div className="max-w-4xl mx-auto px-5 sm:px-8">
        <Reveal>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <Eyebrow>{L.updatedLabel}: {L.updated}</Eyebrow>
            <Link to={`/${other}`} data-testid="legal-switch-link" className="text-sm font-semibold text-slate-500 hover:text-[#7fa315] transition-colors underline-offset-4 hover:underline">
              {L[other].title} ←
            </Link>
          </div>
        </Reveal>
        <Reveal delay={0.08}>
          <div className="mt-6 flex items-center gap-4">
            <span className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#ABCF39] to-[#DAE13B] text-[#383838] flex items-center justify-center shrink-0">
              <Icon size={26} />
            </span>
            <h1 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#383838]" data-testid="legal-title">
              {doc.title}
            </h1>
          </div>
        </Reveal>
        <Reveal delay={0.14}>
          <div className="mt-8 rounded-3xl bg-[#FAFAFA] border border-slate-200/80 p-6 sm:p-8 space-y-3">
            {doc.intro.map((t) => (
              <p key={t} className="text-base sm:text-lg leading-relaxed text-slate-700">{t}</p>
            ))}
          </div>
        </Reveal>
        <div className="mt-14 space-y-12">
          {doc.sections.map((s, i) => (
            <Section key={s.h} index={i} s={s} contactLabel={L.contactLabel} />
          ))}
        </div>
      </div>
    </main>
  );
}
